import { Injectable } from '@angular/core';
import { EnvironmentService } from './environment.service';
import { AuthService } from './auth.service';
import * as _ from 'lodash-es';
import { DistrictModel, SiteModel, SitesModel } from '../models/sites.model';
import { BehaviorSubject, catchError, map, Observable, of, take, throwError } from 'rxjs';
import { UserLoginModel } from '../models/user-login.model';
import { HttpClient } from '@angular/common/http';
import { Util } from '../shared/util';

@Injectable({ providedIn: 'root' })
export class UserService {
  private _enrolledSites: BehaviorSubject<SitesModel> = new BehaviorSubject<SitesModel>(undefined);
  private enrolledSites$: Observable<SitesModel> = this._enrolledSites.asObservable();

  constructor(private authService: AuthService, private http: HttpClient, private envService: EnvironmentService) {
  }

  // Sites
  public getEnrolledSitesInDistrict(): SiteModel[] {
    return _.get(this._enrolledSites.value, 'sites', []);
  }
  public getEnrolledSitesInDistrict$(): Observable<SiteModel[]> {
    return this.enrolledSites$
      .pipe(
        map(s => _.get(s, 'sites', []))
      );
  }
  public setEnrolledSitesInDistrict(sites: SitesModel) {
    this._enrolledSites.next(sites);
  }

  // District Info
  public getDistrictInfo(): DistrictModel {
    return _.get(this._enrolledSites.value, 'district');
  }
  public getDistrictInfo$(): Observable<DistrictModel> {
    return this.enrolledSites$
      .pipe( map(s => _.get(s, 'district')) );
  }

  // SiteByGuid
  public getSite(siteGuid: string): SiteModel {
    return _.find(this.getEnrolledSitesInDistrict(), ['guid', siteGuid]);
  }

  public fetchSitesList(ulm: UserLoginModel): Observable<SitesModel> {
    const existingSites: SitesModel = this._enrolledSites.value;
    if (existingSites) {
      if (ulm?.getSub() === existingSites.userGuid) { // If we've alredy fetched Sites for this UserGuid, no need to fetch them again.
        return of(existingSites);
      }
    }

    if (ulm) {
      return this.fetchEnrolledSitesForUser(ulm);
     } else {
      return this.fetchAllSitesInDistrict(ulm);
    }
  }

  private fetchAllSitesInDistrict(ulm?: UserLoginModel): Observable<SitesModel> {

    if (this.authService.getDistrictAppId()) {
      return this.http.get<SitesModel>(this.envService.getPortalBackendUrl() + '/user/district/' + this.authService.getDistrictAppId() +'/sites', { withCredentials: true })
        .pipe(
          map((sites: SitesModel) => {
            if (ulm?.isDistrictUser()) {
              sites.sites = this.filterSitesForDistrictUser(sites.sites, ulm);
            }
            sites.userGuid = ulm?.getSub();
            this.setEnrolledSitesInDistrict(sites);
            return sites;
          }),
          Util.getRetryPipe(24, 5000, 429),
          catchError((err) => {
            this.authService.logFailInfo(undefined, {fn: 'fetchAllSitesInDistrict'}, err).pipe(take(1)).subscribe();
            this.setEnrolledSitesInDistrict(undefined);
            return throwError(() => err);
          })
        );
    }
    return of(undefined);
  }

  private filterSitesForDistrictUser(sites: SiteModel[], ulm: UserLoginModel): SiteModel[] {
    return sites.filter(s => {
      if (s.mediaSite || s.districtAdvancedBooking) {
        return ulm.isDestinyAdmin();
      }
      return true;
    });
  }

  private fetchEnrolledSitesForUser(ulm: UserLoginModel): Observable<SitesModel> {
    const params = {
      access_token: this.authService.getAccessToken(),
      district_id: ulm.getAppId(),
      user_guid: ulm.getSub()
    };

    if (!ulm?.isGuestUser() && !_.isEmpty(params.district_id) && !_.isEmpty(params.user_guid)) {
      return this.http.get<SitesModel>(this.envService.getPortalBackendUrl() + '/user/sites', { params, withCredentials: true })
        .pipe(
          map((sites: SitesModel) => {
            if (ulm?.isDistrictUser()) {
              sites.sites = this.filterSitesForDistrictUser(sites.sites, ulm);
            }
            sites.userGuid = ulm.getSub();
            this.setEnrolledSitesInDistrict(sites);
            return sites;
          }),
          Util.getRetryPipe(24, 5000, 429),
          catchError((err) => {
            this.authService.logFailInfo(undefined, {fn: 'fetchEnrolledSitesForUser'}, err).pipe(take(1)).subscribe();
            this.setEnrolledSitesInDistrict(undefined);
            return throwError(() => err);
          })
        );
    }
    return of(undefined);
  }

}
